import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Outline = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">✏️ CSS Outline</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        A <strong>CSS outline</strong> is a line drawn around an element,
        outside the border. Outlines are often used for accessibility (e.g.,
        highlighting focused elements). Unlike borders:
      </p>
      <ul className="tutorial-list">
        <li>Outlines do not take up space (they don’t affect layout).</li>
        <li>They can’t be set for individual sides (always apply to all).</li>
      </ul>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`selector {
  outline-style: solid | dotted | dashed | double | groove | none;
  outline-color: color;
  outline-width: thin | medium | thick | length;
}`}
        />
      </div>

      {/* Outline Style */}
      <div className="tutorial-section">
        <h2 className="section-title">Outline Style</h2>
        <CodeCopyBlock
          code={`p {
  outline-style: dotted;
}`}
        />
        <p
          style={{
            border: "2px solid #333",
            outlineStyle: "dotted",
            outlineColor: "#0ea5e9",
            outlineWidth: "2px",
            padding: "10px",
          }}
        >
          This paragraph has a <strong>dotted outline</strong>.
        </p>
      </div>

      {/* Outline Color & Width */}
      <div className="tutorial-section">
        <h2 className="section-title">Outline Color & Width</h2>
        <CodeCopyBlock
          code={`div {
  outline: solid red 4px;
}`}
        />
        <div
          style={{
            border: "2px solid #333",
            outline: "4px solid red",
            padding: "15px",
            background: "#fee2e2",
          }}
        >
          Solid red outline (4px wide)
        </div>
      </div>

      {/* Outline Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Outline Shorthand</h2>
        <p>
          You can set <code>outline-color</code>, <code>outline-style</code>,
          and <code>outline-width</code> all in one line.
        </p>
        <CodeCopyBlock
          code={`div {
  outline: blue dashed 3px;
}`}
        />
        <div
          style={{
            border: "2px solid #333",
            outline: "3px dashed blue",
            padding: "15px",
            background: "#dbeafe",
          }}
        >
          Outline shorthand: dashed blue 3px
        </div>
      </div>

      {/* Outline Offset */}
      <div className="tutorial-section">
        <h2 className="section-title">Outline Offset</h2>
        <p>
          The <code>outline-offset</code> property adds space between the
          element’s border and the outline.
        </p>
        <CodeCopyBlock
          code={`div {
  outline: solid green 3px;
  outline-offset: 10px;
}`}
        />
        <div
          style={{
            border: "2px solid #333",
            outline: "3px solid green",
            outlineOffset: "10px",
            padding: "15px",
            background: "#dcfce7",
          }}
        >
          Green outline with 10px offset
        </div>
      </div>

      {/* Accessibility Example */}
      <div className="tutorial-section">
        <h2 className="section-title">Accessibility Use Case</h2>
        <p>
          Outlines are often used to show <strong>keyboard focus</strong>. For
          example:
        </p>
        <CodeCopyBlock
          code={`button:focus {
  outline: 3px solid orange;
}`}
        />
        <button
          style={{
            padding: "10px 20px",
            border: "2px solid #333",
            background: "#fef3c7",
          }}
        >
          Focus me (try Tab key)
        </button>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you know about outlines, explore{" "}
          <a href="/css/text" className="learn-more">
            CSS Text
          </a>{" "}
          to learn about text styling and decoration.
        </p>
      </div>
    </div>
  );
};

export default CSS_Outline;
